import { Add, Information, TrashCan } from '@carbon/icons-react';
import { Button, Drawer, useNotifications } from '@ph-react-ui/core';
import { useQueryClient } from '@tanstack/react-query';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useCreatePaymentScheme } from '../../../hooks/payment-schemes/useCreateScheme';
import { useGetPaymentSchemeById } from '../../../hooks/payment-schemes/useGetSchemeById';
import { useUpdatePaymentSchemeById } from '../../../hooks/payment-schemes/useUpdateScheme';
import { useCancellationDialog } from '../../../hooks/useCancellationDialog';
import { useModal } from '../../../hooks/useModal';
import type { PaymentSchemeEditDto } from '../../../models/payment-schemes/payment-scheme-edit.dto';
import { Input, Select } from '../../../utils/components/hoc-components';
import { CREATE_EDIT_SCHEME } from '../../../utils/constants/payment-schemes/modals';
import { PAYMENT_INSTALLMENTS } from '../../../utils/constants/payment-schemes/payment-installments';
import {
  getCommonValidators,
  VALIDATION as V,
} from '../../../utils/inputValidators';
import { FormSkeleton } from '../../shared/components/skeletons/FormSkeleton';

export const CreateEditScheme = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const notification = useNotifications();

  const id = searchParams.get('schemeId');
  const { close: closeModal } = useModal(CREATE_EDIT_SCHEME);
  const { open: openCancellationDialog } = useCancellationDialog();
  const { data: schemeData, isLoading } = useGetPaymentSchemeById(
    id,
    Boolean(id)
  );

  const createScheme = useCreatePaymentScheme();
  const updateScheme = useUpdatePaymentSchemeById(id);

  const methods = useForm({
    defaultValues: {
      name: '',
      templateSchemeEntries: [
        { amount: null, id: 0 },
        { amount: null, id: 1 },
      ],
    },
    values: {
      name: schemeData?.name || '',
      templateSchemeEntries: schemeData?.templateSchemeEntries.map(
        (e, index) => {
          return {
            amount: e.amount,
            id: index,
          };
        }
      ) || [
        { amount: null, id: 0 },
        { amount: null, id: 1 },
      ],
    },
  });

  const { append, remove } = useFieldArray({
    name: 'templateSchemeEntries',
    control: methods.control,
  });

  const schemesWatch = methods.watch('templateSchemeEntries');
  const { isDirty } = methods.formState;
  const isPerformingApiRequest =
    createScheme.isPending || updateScheme.isPending;

  const isAmountCorrect =
    schemesWatch
      .map((e) => (typeof e.amount === 'number' ? e.amount : 0))
      .reduce((a, c) => a + c, 0) === 100;

  const options = PAYMENT_INSTALLMENTS.map((p) => ({
    label: `${p}%`,
    value: p,
  }));

  const onCloseDrawer = () => {
    if (isDirty) {
      openCancellationDialog();
    } else {
      closeModal();
    }
  };

  const onSubmit = async (data: {
    name: string;
    templateSchemeEntries: { amount: number | null }[];
  }) => {
    const dto: PaymentSchemeEditDto = {
      name: data.name,
      yearlyPrice: 0,
      discount: 0,
      gracePeriod: 0,
      templateSchemeEntries: data.templateSchemeEntries.map((e) => ({
        amount: e.amount!,
        dueMonth: 1,
        dueDay: 1,
        penalty: 0,
      })),
    };

    if (id) {
      updateSchemeData(dto);
    } else {
      createNewScheme(dto);
    }
  };

  const createNewScheme = async (data: PaymentSchemeEditDto) => {
    await createScheme
      .mutateAsync(data)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['payment-schemes'] });
        closeModal();
        notification.success(
          t('PAYMENT_SCHEMES.DRAWER.MESSAGES.SCHEME_CREATED')
        );
      })
      .catch((resError) => {
        notification.danger(t(`NETWORK_ERRORS.${resError.errors[0]}`));
      });
  };

  const updateSchemeData = async (data: PaymentSchemeEditDto) => {
    await updateScheme
      .mutateAsync(data)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['payment-schemes', id] });
        queryClient.invalidateQueries({ queryKey: ['payment-schemes'] });
        closeModal();
        notification.success(
          t('PAYMENT_SCHEMES.DRAWER.MESSAGES.SCHEME_UPDATED')
        );
      })
      .catch((resError) => {
        notification.danger(t(`NETWORK_ERRORS.${resError.errors[0]}`));
      });
  };

  if (isLoading && id) {
    return <FormSkeleton />;
  }

  return (
    <Drawer
      header={
        id
          ? t('PAYMENT_SCHEMES.DRAWER.TITLES.EDIT_SCHEME')
          : t('PAYMENT_SCHEMES.DRAWER.TITLES.CREATE_SCHEME')
      }
      footer={
        <div className="drawer__payment-schemes__form__actions">
          <Button onClick={onCloseDrawer} type="button" variant="outlined">
            {t('ACTIONS.CLOSE')}
          </Button>
          <Button
            form="payment-scheme-form"
            loading={isPerformingApiRequest}
            disabled={!isDirty || !isAmountCorrect || isPerformingApiRequest}
            onClick={methods.handleSubmit((data) => onSubmit(data))}
          >
            {id ? t('ACTIONS.UPDATE') : t('ACTIONS.SAVE')}
          </Button>
        </div>
      }
    >
      <FormProvider {...methods}>
        <form
          id="payment-scheme-form"
          className="drawer__payment-schemes__form"
        >
          <Input
            name="name"
            className="drawer__payment-schemes__form__input mb-6"
            label={t('PAYMENT_SCHEMES.DRAWER.LABELS.NAME')!}
            placeholder={t('PAYMENT_SCHEMES.DRAWER.PLACEHOLDERS.NAME')!}
            rules={getCommonValidators([V.REQUIRED, V.MAX_LENGTH])}
          />
          <div className="drawer__payment-schemes__form__information mb-4">
            <Information size={20} className="mr-2"></Information>
            <div className="drawer__payment-schemes__form__information_text">
              <p className="mb-1">
                {t('PAYMENT_SCHEMES.DRAWER.MESSAGES.MIN_MAX_PAYMENTS')}
              </p>
              <p>{t('PAYMENT_SCHEMES.DRAWER.MESSAGES.SUM_OF_ALL')}</p>
            </div>
          </div>
          {schemesWatch.map((item, index) => {
            return (
              <div
                className="drawer__payment-schemes__form__installment mb-6"
                key={index}
              >
                <Select
                  key={item.id}
                  className="drawer__payment-schemes__form__installment__select"
                  label={t('PAYMENT_SCHEMES.DRAWER.LABELS.INSTALLMENT')!}
                  placeholder={
                    t('PAYMENT_SCHEMES.DRAWER.PLACEHOLDERS.INSTALLMENT')!
                  }
                  name={`templateSchemeEntries.${index}.amount`}
                  options={options}
                  rules={getCommonValidators([V.REQUIRED])}
                ></Select>
                <Button
                  className="drawer__payment-schemes__form__installment__trash"
                  variant="link"
                  color="danger"
                  onClick={() => remove(index)}
                  disabled={schemesWatch.length <= 2}
                >
                  <TrashCan size={24} />
                </Button>
              </div>
            );
          })}
          <div className="drawer__payment-schemes__form__add">
            <Button
              type="button"
              className="mb-4"
              variant="outlined"
              onClick={() =>
                append({
                  amount: null,
                  id: schemesWatch[schemesWatch.length - 1].id + 1,
                })
              }
              disabled={schemesWatch.length >= 20}
            >
              <Add /> {t('PAYMENT_SCHEMES.DRAWER.ACTIONS.ADD_AN_INSTALLMENT')}
            </Button>
          </div>
        </form>
      </FormProvider>
    </Drawer>
  );
};
